import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useDemoStore } from '../../store/demoFlowStore';
import {
  Home,
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  Package,
  IndianRupee,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
  Layers,
  CreditCard,
  CheckSquare,
  LogOut,
  BookOpen,
} from 'lucide-react';

const allRoles = ['admin', 'owner', 'designer', 'accountant', 'installation_team', 'client_coordinator', 'store_incharge', 'sales_executive', 'field_marketing', 'social_media', 'hr'];

const navItems = [
  { label: 'Home', path: '/home', icon: <Home size={16} />, roles: allRoles },
  { label: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={16} />, roles: allRoles },

  // Space Designs Dropdown child items
  { label: 'Teams', path: '/teams', icon: <Users size={16} />, roles: allRoles },
  { label: 'Customers', path: '/customers', icon: <Users size={16} />, roles: allRoles },
  { label: 'Approvals', path: '/approvals', icon: <CheckSquare size={16} />, roles: allRoles },
  { label: 'Inventory', path: '/inventory', icon: <Package size={16} />, roles: allRoles },
  // { label: 'Expenses', path: '/expenses', icon: <CreditCard size={16} />, roles: allRoles },
  { label: 'Accounts', path: '/accounts', icon: <IndianRupee size={16} />, roles: allRoles },
  { label: 'Book an Entry', path: '/book-entry', icon: <BookOpen size={16} />, roles: allRoles },
  { label: 'Documents', path: '/documents', icon: <FileText size={16} />, roles: allRoles },

  // Settings & Notifications
  { label: 'Notifications', path: '/notifications', icon: <Bell size={16} />, roles: allRoles },
  { label: 'Settings', path: '/settings', icon: <Settings size={16} />, roles: allRoles },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { companyLogo } = useDemoStore();
  const logoSrc = companyLogo || require('../../assets/Images/space-designs-logo.png');

  const [logoError, setLogoError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const spaceDesignsItems = ['Teams', 'Customers', 'Approvals', 'Inventory', 'Expenses', 'Accounts', 'Book an Entry'];

  const checkIsActive = (item) => {
    const fullLocation = location.pathname + location.search;
    if (item.path.includes('?')) {
      return fullLocation === item.path;
    }
    return location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
  };

  const isInsideSpaceDesigns = () => {
    return navItems.some(item => checkIsActive(item) && spaceDesignsItems.includes(item.label));
  };

  const [isOpen, setIsOpen] = useState(true);

  const [permissions, setPermissions] = useState({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (isInsideSpaceDesigns()) {
      setIsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user?.role && user.role !== 'owner') {
      import('../../services/api').then(({ default: api }) => {
        api.get('/role-access/matrix-data').then(res => {
          if (res.data?.success) {
            setPermissions(res.data.data.matrix[user.role] || {});
            setPermissionsLoaded(true);
          }
        }).catch(() => {
          setPermissionsLoaded(true);
        });
      });
    } else {
      setPermissionsLoaded(true);
    }
  }, [user?.role]);

  const filteredNav = navItems.filter(item => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    if (!permissionsLoaded) return false;
    // Default to true if not found in matrix (failsafe), otherwise check 'view' permission
    const perm = permissions[item.label];
    return perm ? perm.view : false;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'SD';
    const parts = user.name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.name.slice(0, 2).toUpperCase();
  };

  const NavLink = ({ item, isNested = false }) => {
    const isActive = checkIsActive(item);

    return (
      <Link
        to={item.path}
        title={item.label}
        className={`sidebar-link group relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-all duration-200 cursor-pointer ${
          isActive
            ? 'active bg-[#C91D25] !text-white shadow-[0_4px_14px_rgba(201,29,37,0.25)] font-semibold'
            : 'text-[#626262] hover:bg-[#F7F3EC] hover:text-[#C91D25]'
        } ${collapsed ? 'justify-center px-0 py-2.5' : ''}`}
      >
        <span
          className={`shrink-0 transition-transform duration-200 group-hover:scale-110 ${
            isActive ? '!text-white' : 'text-[#A27327]/70 group-hover:text-[#C91D25]'
          }`}
        >
          {item.icon}
        </span>
        {!collapsed && <span className={`truncate tracking-tight font-sans ${isActive ? '!text-white font-semibold' : ''}`}>{item.label}</span>}
        {collapsed && (
          <span className="absolute left-full ml-3 px-3 py-1.5 bg-[#1A1A1A] text-white text-[12px] font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50 shadow-xl border border-slate-800">
            {item.label}
          </span>
        )}
      </Link>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-white/95 backdrop-blur-md border-r border-[#ECE7DE] shadow-[1px_0_12px_rgba(0,0,0,0.03)] flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-[64px]' : 'w-[260px]'
      }`}
    >
      {/* Logo Area */}
      <div
        className={`relative flex items-center h-[64px] px-3 border-b border-[#ECE7DE] flex-shrink-0 ${
          collapsed ? 'justify-center px-0' : 'justify-between'
        }`}
      >
        {collapsed ? (
          <div className="flex items-center justify-center">
            <img
              src={logoSrc}
              alt="Space Designs"
              className="h-8 w-auto rounded-md object-contain select-none shadow-xs"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 select-none min-w-0">
            <span className="font-serif text-[15px] font-bold tracking-tight text-[#1A1A1A] leading-none shrink-0">
              Space <span className="text-[#C91D25]">Designs</span>
            </span>
            <img
              src={logoSrc}
              alt="Space Designs Logo"
              className="h-7 w-auto rounded-md object-contain select-none shrink-0 shadow-xs border border-[#ECE7DE]"
            />
          </div>
        )}

        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg text-[#626262] hover:text-[#C91D25] hover:bg-[#F7F3EC] transition-all duration-200"
            title="Collapse sidebar"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 space-y-1 custom-scrollbar">
        {/* Top: Home, Dashboard */}
        {filteredNav
          .filter((item) => ['Home', 'Dashboard'].includes(item.label))
          .map((item) => (
            <NavLink key={item.path} item={item} />
          ))}

        {/* Space Designs Submenu Accordion */}
        {!collapsed ? (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 mt-4 mb-1 rounded-xl transition-all duration-200 ${
              isInsideSpaceDesigns()
                ? 'bg-[#F7F3EC] text-[#C91D25] font-semibold'
                : 'hover:bg-[#F7F3EC]/70 text-[#626262] hover:text-[#C91D25]'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={`shrink-0 transition-colors ${
                  isInsideSpaceDesigns() ? 'text-[#C91D25]' : 'text-[#A27327]'
                }`}
              >
                <Layers size={16} />
              </span>
              <span className="text-[11px] uppercase tracking-wider font-bold text-[#A27327]">
                Space Designs
              </span>
            </div>
            <ChevronDown
              size={14}
              className={`text-[#A27327] transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
        ) : (
          <div className="my-2" />
        )}

        {/* Dropdown Items */}
        {(collapsed || isOpen) && (
          <div className={!collapsed ? "pl-3 ml-3 my-1 space-y-1" : "space-y-1"}>
            {filteredNav
              .filter((item) => spaceDesignsItems.includes(item.label))
              .map((item) => (
                <NavLink key={item.path} item={item} isNested={!collapsed} />
              ))}
          </div>
        )}

        {/* Bottom items: Documents, Notifications, Settings */}
        {filteredNav
          .filter((item) => ['Documents', 'Notifications', 'Settings'].includes(item.label))
          .map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
      </nav>

      {/* Expand Button for Collapsed Mode */}
      {collapsed && (
        <div className="p-2 flex justify-center border-t border-[#ECE7DE]">
          <button
            onClick={onToggle}
            className="p-2 rounded-xl text-[#626262] hover:text-[#C91D25] hover:bg-[#F7F3EC] transition-all duration-200"
            title="Expand sidebar"
          >
            <PanelLeftOpen size={16} />
          </button>
        </div>
      )}

      {/* User profile Footer */}
      <div className={`p-3 flex-shrink-0 border-t border-[#ECE7DE] ${collapsed ? 'flex flex-col items-center gap-2' : ''}`}>
        <div className={`rounded-2xl transition-all duration-200 ${collapsed ? '' : 'p-2.5 bg-[#F7F3EC]/70 border border-[#ECE7DE] hover:bg-[#F7F3EC] flex items-center justify-between gap-3'}`}>
          <div className="flex items-center gap-3 min-w-0">
            {!avatarError && user?.avatar ? (
              <img
                src={user.avatar}
                alt={user?.name || "User"}
                onError={() => setAvatarError(true)}
                className="w-9 h-9 rounded-full object-cover shrink-0 select-none shadow-xs border border-[#C99B63]/40"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#C91D25] to-[#A27327] text-white font-bold text-[12px] flex items-center justify-center shadow-xs shrink-0 select-none border border-white/30">
                {getUserInitials()}
              </div>
            )}

            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1A1A] tracking-tight truncate leading-tight">
                  {user?.name || 'Space Designs Owner'}
                </div>
                <div className="text-[11px] font-medium text-[#A27327] tracking-wide truncate capitalize mt-0.5">
                  {user?.role ? user.role.replace(/_/g, ' ') : 'Owner'}
                </div>
              </div>
            )}
          </div>

          {!collapsed && (
            <button
              onClick={handleLogout}
              className="p-1.5 text-[#626262] hover:text-[#C91D25] hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          )}

          {collapsed && (
            <button
              onClick={handleLogout}
              className="p-2 text-[#626262] hover:text-[#C91D25] hover:bg-red-50 rounded-xl transition-colors mt-1"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};





